import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// api
import ApiKey from '../../api/ApiKey';
import BaseApi from '../../api/BaseApi';
// components
import Footer from '../../element/Footer';
import NavBar from '../../element/NavBar';
import CVDescription from '../../element/CVDescription';
// helpers
import { logOutIfTokenExpired, resultInfo } from '../../helpers';
// hooks
import { useWindowSize } from '../../../Hooks/use-window-size';
// mui
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
// import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

const ViewCV = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const { t } = useTranslation('global');
  const userType = Cookies.get('user_type');
  const tokenKey = Cookies.get('tokenClient');

  const [loading, setLoading] = useState(true);
  const [cvDetails, setCvDetails] = useState({});
  const [folderName, setFolderName] = useState('');
  const [foldersList, setFoldersList] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);

  const { fav_status, candidate_id } = cvDetails;

  const getFoldersList = async () => {
    try {
      const response = await axios.get(
        BaseApi + '/folder/list',
        {
          headers: {
            'Content-Type': 'application/json',
            key: ApiKey,
            token: tokenKey,
          },
        },
      );

      if (response.status === 200) {
        setFoldersList(response.data);
      } else {
        resultInfo('error', response.data.message, t('jobseekerManageAlert.close'));
      }
    } catch (error) {
      console.error('Cannot get folders list', error);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        BaseApi + '/candidates/getCv',
        { id },
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      setLoading(false);

      if (response.data.status === 200) {
        setCvDetails(response.data.response);
      } else if (response.data.status === 400) {
        navigate('/');

        Cookies.remove('fname');
        Cookies.remove('user_id');
        Cookies.remove('user_type');
        Cookies.remove('tokenClient');

        resultInfo('warning', response.data.message, t('jobseekerManageAlert.close'));
      } else {
        resultInfo('error', response.data.message, t('jobseekerManageAlert.close'));
      }
    } catch (error) {
      setLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      }

      console.error('Cannot get profile data', error);
    }
  };

  const handleChange = e => setFolderName(e.target.value);

  const handleCreateFolder = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        BaseApi + '/folder/create',
        {
          name: folderName,
        },
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      setLoading(false);

      if (response.status === 200) {
        resultInfo('success', t('employerFavouriteList.folderCreated'), t('employerFavouriteList.close'));
        setFolderName('');
        setShowCreateFolder(false);
        getData();
        getFoldersList();
      } else {
        Swal.fire({
          icon: 'error',
          text: response.data.message,
          title: t('employerFavouriteList.createFolderFailedTitle'),
          confirmButtonText: t('employerFavouriteList.close'),
        });
      }
    } catch (error) {
      setLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      } else {
        Swal.fire({
          icon: 'error',
          text: t('employerFavouriteList.folderFailedTxt'),
          confirmButtonText: t('employerFavouriteList.close'),
          title: t('employerFavouriteList.createFolderFailedTitle'),
        });
      }

      console.error('Couldn\'t create the folder!', error);
    }
  };

  const handleFavourite = async () => {
    try {
      const response = await axios.post(
        BaseApi + `/candidates/addtoFavorite/${candidate_id}/${id}`,
        {
          selectedFolderId,
          from_cv_database: 1,
        },
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      if (response.data.status === 200) {
        resultInfo('success', t('employerFavouriteListProfile.addFavSuccessTitle'), t('employerFavouriteListProfile.close')); // eslint-disable-line

        getData();
      } else if (response.data.status === 400) {
        navigate('/');

        Cookies.remove('fname');
        Cookies.remove('user_id');
        Cookies.remove('user_type');
        Cookies.remove('tokenClient');

        resultInfo('warning', response.data.message, t('employerFavouriteListProfile.close'));
      } else {
        Swal.fire({
          icon: 'error',
          text: response.data.message,
          title: t('employerFavouriteListProfile.addFavFailedTitle'),
          confirmButtonText: t('employerFavouriteListProfile.close'),
        });
      }
    } catch (error) {
      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      }

      Swal.fire({
        icon: 'error',
        text: t('employerFavouriteListProfile.addFavFailedTxt'),
        confirmButtonText: t('employerFavouriteListProfile.close'),
        title: t('employerFavouriteListProfile.addFavFailedTitle'),
      });
    }
  };

  useEffect(() => {
    if (userType === 'candidate' || !tokenKey) {
      navigate('/user/jobseekerlogin');
    } else {
      getData();
      getFoldersList();
      window.scrollTo(0, 0);
    }
  }, [tokenKey, navigate, userType]); // eslint-disable-line

  return (
    <>
      <NavBar />
      {
        loading
          ? (
            <div className='loader-container'></div>
          ) : (
            <>
              <div className='container my-5 JSMyCV'>
                <div className='row'>
                  <div className='col cheaps'>
                    {/* <button className='btn btn-primary button2' onClick={() => navigate('/candidates/listing')}>
                      <ChevronLeftOutlinedIcon />
                      {' '}
                      {width >= 768 && t('cvDescription.back')}
                    </button> */}
                    {
                      fav_status === 1
                        ? (
                          <button disabled className='btn btn-primary button1'>
                            <StarOutlinedIcon />
                            {' '}
                            {width >= 768 && t('cvDescription.removeFromFavourite')}
                          </button>
                        ) : (
                          <>
                            <button
                              type='button'
                              data-bs-toggle='dropdown'
                              aria-expanded={isDropdownOpen}
                              className='btn-primary button1'
                              onClick={() => {
                                setSelectedFolderId(null);
                                setIsDropdownOpen(!isDropdownOpen);
                              }}
                            >
                              <StarOutlineOutlinedIcon />
                              {' '}
                              {width >= 768 && t('cvDescription.addToFavourite')}
                            </button>
                            <div className='p-4 dropdown-menu' onClick={e => e.stopPropagation()}>
                              <div className='foldersList' onClick={e => e.stopPropagation()}>
                                {
                                  foldersList?.map(folder => (
                                    <button
                                      key={folder.id}
                                      className={`px-1 foldersListItemButton ${folder.id === selectedFolderId && 'active'}`}
                                      onClick={e => {
                                        e.stopPropagation();
                                        setSelectedFolderId(folder.id);
                                      }}
                                    >
                                      {folder.name}
                                    </button>
                                  ))
                                }
                              </div>
                              <button className='btn button1 mb-1' onClick={handleFavourite} disabled={!selectedFolderId}>
                                {t('cvDescription.addToFavourite')}
                              </button>
                              <hr />
                              {
                                !showCreateFolder
                                  ? (
                                    <button
                                      className='favouriteListFoldersButton mt-0 ms-2'
                                      onClick={() => setShowCreateFolder(true)}
                                    >
                                      <CreateNewFolderIcon sx={{ mr: '8px' }} />
                                      {t('cvDescription.createFolder')}
                                    </button>
                                  ) : (
                                    <div>
                                      <input
                                        type='text'
                                        name='folderName'
                                        value={folderName}
                                        onChange={handleChange}
                                        className='form-control createFolderInput'
                                        placeholder={t('employerFavouriteList.folderName')}
                                      />
                                      <button
                                        type='button'
                                        disabled={!folderName}
                                        onClick={handleCreateFolder}
                                        className='favouriteListFoldersButton'
                                      >
                                        {t('employerFavouriteList.create')}
                                      </button>
                                    </div>
                                  )
                              }
                            </div>
                          </>
                        )
                    }
                    <button disabled className='btn btn-primary button1'>
                      <FileDownloadOutlinedIcon />
                      {' '}
                      {width >= 768 && t('cvDescription.downloadPDF')}
                    </button>
                  </div>
                </div>
                <CVDescription cvDetails={cvDetails} />
              </div>
              <Footer />
            </>
          )
      }
    </>
  );
};

export default ViewCV;
